import {
  faClockRotateLeft,
  faFilter,
  faPenToSquare,
  faRightFromBracket,
  faRightToBracket,
} from "@fortawesome/free-solid-svg-icons"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { FloatButton } from "../../components/FloatButton/FloatButton"
import { PopupHelpButton } from "../../components/Help/PopupHelpButton"
import List from "../../components/List/List"
import { useMenuMobileContext } from "../../context/useMenuMobileContext"
import { useTimeClockStatus } from "../../context/useTimeClockStatusContext"
import THEME from "../../configuration/Theme"
import { useError } from "../../context/useErrorContext"
import { useTimeClock } from "../../hooks/TimeClock/useTimeClock"
import { companyMemory } from "../../Objects/Memory/Memory"
import { getTimeClockEntryCard } from "../../Objects/TimeClock/TimeClockCards"
import type { TimeClockPunchPayload } from "../../Objects/TimeClock/TimeClockPunchPayload"
import type { TimeClockReport } from "../../Objects/TimeClock/TimeClockReport"
import type { CreateTimeClockAdjustment } from "../../Objects/TimeClock/CreateTimeClockAdjustment"
import type { TimeClockEntry } from "../../Objects/TimeClock/TimeClockEntry"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import { getDateKeyInTimeZone } from "../../utils/timeZone"
import { useQuickActionPopup } from "../../hooks/useQuickActionPopup"
import { TimeClockAdjustmentPopup } from "./TimeClockAdjustmentPopup"
import { TimeClockMyAdjustmentsPopup } from "./TimeClockMyAdjustmentsPopup"
import { TimeClockPeriodFilterPopup } from "./TimeClockPeriodFilterPopup"
import { TimeClockPunchWizardPopup } from "./TimeClockPunchWizardPopup"

export const MyTimeClockPage = () => {
  const { t, i18n } = useTranslation(["timeClock", "general", "helpPopup", "list"])

  const { setError } = useError()

  const { isNotMobile } = useMenuMobileContext()

  const { status, refresh: refreshStatus } = useTimeClockStatus()

  const { punch, getMyReport, createMyAdjustment } = useTimeClock()

  const [report, setReport] = useState<TimeClockReport | undefined>(undefined)

  const [isPunchOpen, setIsPunchOpen] = useState(false)

  const [isAdjustmentOpen, setIsAdjustmentOpen] = useState(false)

  const [isMyAdjustmentsOpen, setIsMyAdjustmentsOpen] = useState(false)

  const [isPeriodFilterOpen, setIsPeriodFilterOpen] = useState(false)

  const [isPunching, setIsPunching] = useState(false)

  const [startDate, setStartDate] = useState(
    () => `${getDateKeyInTimeZone(new Date(), companyMemory.timeZoneId).slice(0, 8)}01`,
  )

  const [endDate, setEndDate] = useState(() => getDateKeyInTimeZone(new Date(), companyMemory.timeZoneId))

  useQuickActionPopup(
    "time-clock-request-adjustment",
    useCallback(() => setIsAdjustmentOpen(true), []),
  )

  const loadReport = useCallback(async () => {
    const result = await getMyReport(startDate, endDate)

    if (result) setReport(result)
  }, [endDate, getMyReport, startDate])

  useEffect(() => {
    void loadReport()
  }, [loadReport])

  const entries: TimeClockEntry[] = useMemo(() => report?.days.flatMap((day) => day.entries) ?? [], [report])

  const cards = useMemo(
    () => entries.map((entry, index) => getTimeClockEntryCard(entry, index, t, i18n.language)),
    [entries, t, i18n.language],
  )

  const getPosition = () =>
    new Promise<GeolocationPosition | null>((resolve) => {
      if (!navigator.geolocation) {
        resolve(null)

        return
      }

      navigator.geolocation.getCurrentPosition(
        (position) => resolve(position),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
      )
    })

  const executePunch = async (payload: TimeClockPunchPayload) => {
    if (!status || isPunching) return false

    setIsPunching(true)

    try {
      const requiresGeolocation = status.requiredValidations.includes("Geolocation")

      let position: GeolocationPosition | null = null

      if (requiresGeolocation) {
        position = await getPosition()

        if (!position) {
          setError(t("general:label_title_erro"), t("timeClock:my.location_denied"))

          return false
        }
      }

      const entry = await punch({
        ...payload,
        direction: status.nextDirection,
        latitude: position?.coords.latitude,
        longitude: position?.coords.longitude,
        accuracyMeters: position?.coords.accuracy,
      })

      if (!entry) return false

      await refreshStatus()

      await loadReport()

      return true
    } finally {
      setIsPunching(false)
    }
  }

  const handleCreateAdjustment = async (request: CreateTimeClockAdjustment) => {
    const created = await createMyAdjustment(request)

    if (!created) return false

    setIsAdjustmentOpen(false)

    return true
  }

  const isIn = status?.nextDirection === "In"

  const periodFilterButtonBottom = `${(isNotMobile ? 5 : 70) + 4 * (isNotMobile ? 90 : 55)}px`

  return (
    <>
      <div className={defaultStyle.containerList}>
        <List
          enableLocalPagination
          disableLocalOrdenation
          showFilterStatus={false}
          messageCount={t("timeClock:my.entries_count")}
          messageEmpty={t("timeClock:my.no_entries")}
          nameFindSearch={t("timeClock:my.search_entries")}
          data={cards}
          handleRefreshButton={() => {
            void refreshStatus()
            void loadReport()
          }}
          handleExtraFloatButton={() => status?.isEnabled && setIsPunchOpen(true)}
          extraFloatButtonIcon={isIn ? faRightToBracket : faRightFromBracket}
          extraFloatButtonColor={isIn ? THEME.colors.buttonBGSaveColor : THEME.colors.buttonBGCloseColor}
          extraFloatButtonDescriptionKey="interactionDescriptions:pages.timeClock.myTimeClockPage.button.punch"
          handleSecondaryExtraFloatButton={() => setIsAdjustmentOpen(true)}
          secondaryExtraFloatButtonIcon={faPenToSquare}
          secondaryExtraFloatButtonColor="#8b5cf6"
          secondaryExtraFloatButtonDescriptionKey="interactionDescriptions:pages.timeClock.myTimeClockPage.button.requestAdjustment"
          handleImportFloatButton={() => setIsMyAdjustmentsOpen(true)}
          importFloatButtonIcon={faClockRotateLeft}
          importFloatButtonColor="#0ea5e9"
          importFloatButtonDescriptionKey="interactionDescriptions:pages.timeClock.myTimeClockPage.button.openMyAdjustments"
          handleOnClick={() => {}}
          filterActions={
            <PopupHelpButton
              variant="global"
              helpKey="pages.my-time-clock"
              title={t("helpPopup:global.myTimeClock.title")}
              description={t("helpPopup:global.myTimeClock.description")}
              bullets={t("helpPopup:global.myTimeClock.bullets", { returnObjects: true }) as string[]}
            />
          }
        />

        <FloatButton
          icon={faFilter}
          color="#FFB45C"
          onClick={() => setIsPeriodFilterOpen(true)}
          bottom={periodFilterButtonBottom}
          right="5px"
          iconSize={isNotMobile ? "3x" : "2x"}
          size={isNotMobile ? 85 : 50}
          descriptionKey="interactionDescriptions:pages.timeClock.myTimeClockPage.button.openPeriodFilter"
        />
      </div>

      {isPunchOpen && status && (
        <TimeClockPunchWizardPopup
          status={status}
          isSaving={isPunching}
          onClose={() => setIsPunchOpen(false)}
          onConfirm={async (payload) => {
            const done = await executePunch(payload)

            if (done) setIsPunchOpen(false)

            return done
          }}
        />
      )}

      {isAdjustmentOpen && (
        <TimeClockAdjustmentPopup
          entries={entries}
          onClose={() => setIsAdjustmentOpen(false)}
          onSave={handleCreateAdjustment}
        />
      )}

      {isMyAdjustmentsOpen && <TimeClockMyAdjustmentsPopup onClose={() => setIsMyAdjustmentsOpen(false)} />}

      {isPeriodFilterOpen && (
        <TimeClockPeriodFilterPopup
          initialStartDate={startDate}
          initialEndDate={endDate}
          onClose={() => setIsPeriodFilterOpen(false)}
          onApply={(nextStartDate, nextEndDate) => {
            setStartDate(nextStartDate)
            setEndDate(nextEndDate)
            setIsPeriodFilterOpen(false)
          }}
        />
      )}
    </>
  )
}
